/**
 * What a refusal means to a person — one framing per reason the gateway can
 * give (`DenyReason` in packages/policy/src/decide.ts). Twenty-one, and the set
 * is asserted against the policy's own union in test, so a reason added
 * there without a sentence here fails a build rather than reaching a screen
 * as a bare code.
 *
 * The gateway's own `detail` is rendered verbatim beside these. Nothing here
 * composes a remedy; these are the human framings of the rule that refused,
 * for the person who hit it.
 */

export type Tone = 'danger' | 'warning' | 'neutral';

export interface Denial {
  title: string;
  body: string;
  /** Who can move it. A role, never a name the interface would have to keep current. */
  who: string;
  tone: Tone;
}

export const DENIALS = {
  killswitch_engaged: {
    title: 'Everything is stopped',
    body: 'Someone engaged the platform-wide stop. No agent can act in any department until it is released.',
    who: 'Superadmin',
    tone: 'danger',
  },
  department_suspended: {
    title: 'This department is stopped',
    body: 'A person stopped its agents. Other departments keep running, and nothing here is deleted — its agents simply cannot act until it is started again.',
    who: 'Superadmin',
    tone: 'danger',
  },
  agent_revoked: {
    title: 'That agent no longer has access',
    body: 'Its access was withdrawn. Its history and everything it built are untouched — it simply cannot act.',
    who: 'Superadmin',
    tone: 'danger',
  },
  manifest_not_granted: {
    title: 'That tool was never granted here',
    body: 'The tool exists and this department was not given it. Reuse-first means it probably already does what you need somewhere else.',
    who: 'Department lead',
    tone: 'warning',
  },
  manifest_expired: {
    title: 'That grant has run out',
    body: 'It was granted for a period and the period ended. Nothing was taken away — it lapsed.',
    who: 'Department lead',
    tone: 'warning',
  },
  department_not_permitted: {
    title: "Not this department's to reach",
    body: 'Scopes are held per department. Crossing that line is a change someone approves, not a setting.',
    who: 'Both department leads',
    tone: 'warning',
  },
  department_is_read_only: {
    title: 'This department only reads',
    body: 'Finance reports on the others and writes to none of them (ADR-0037). Nothing here can compose, send or approve — it asks.',
    who: 'Nobody — this is the rule',
    tone: 'neutral',
  },
  scope_not_held: {
    title: 'That scope is not held',
    body: 'Default-deny: nothing is reachable unless it was granted. Widening it is a proposal like any other change.',
    who: 'Department lead',
    tone: 'warning',
  },
  no_valid_assignment: {
    title: 'No live assignment covers this',
    body: 'Capability follows the assignment (§15). The assignment behind this either has not started or has ended.',
    who: 'Department lead',
    tone: 'warning',
  },
  tool_not_admitted: {
    title: 'This tool has not been admitted',
    body: "It is a vendor's tool, and a vendor's tool works only once a superadmin has admitted it to the tool library and signed the assignment that reaches it (ADR-0133).",
    who: 'Superadmin',
    tone: 'warning',
  },
  twin_exceeds_principal: {
    title: 'A twin cannot do what its person cannot',
    body: "The twin's permissions are intersected with its principal's on every call. This was refused because the person it acts for does not hold it either.",
    who: 'Whoever grants the person',
    tone: 'warning',
  },
  a_twin_does_not_send: {
    title: 'A twin prepares; it never sends',
    body: 'A twin writes drafts for its person and nothing leaves until a person sends it. This call reached a send path, so it was refused whatever the tool declared.',
    who: 'Nobody — the person sends it themselves',
    tone: 'neutral',
  },
  method_not_declared: {
    title: 'The tool does not declare that',
    body: 'A manifest names exactly which methods are allowed. Anything else is refused — including a method the upstream service would happily accept.',
    who: 'Nobody — fix the manifest',
    tone: 'neutral',
  },
  path_not_declared: {
    title: 'That destination is not in the manifest',
    body: 'Destinations come from the signed manifest, never from the request. This is what makes a jailbroken model unable to invent a route.',
    who: 'Nobody — fix the manifest',
    tone: 'neutral',
  },
  query_not_declared: {
    title: 'That query is not in the manifest',
    body: 'A manifest declares which query parameters a call may carry. One it does not name is refused, however harmless it looks.',
    who: 'Nobody — fix the manifest',
    tone: 'neutral',
  },
  tool_not_declared: {
    title: 'That tool is not in the manifest',
    body: 'The transport allows a fixed set of tools and this is not one of them.',
    who: 'Nobody — fix the manifest',
    tone: 'neutral',
  },
  mcp_secret_not_supported: {
    title: 'That server cannot be given a credential this way',
    body: 'An MCP route is decided at the gateway and opened by work-api; a secret the manifest names cannot travel that path yet.',
    who: 'Platform',
    tone: 'neutral',
  },
  body_too_large: {
    title: 'Too much at once',
    body: 'There is a ceiling on how much can move in one call. Ask for it in pieces.',
    who: 'Nobody — try smaller',
    tone: 'neutral',
  },
  supervision_required: {
    title: 'A person has to authorise this',
    body: 'Critical operations consume a single-use token a human mints for this exact operation and target. There is no way to grant it in advance.',
    who: 'Superadmin',
    tone: 'danger',
  },
  privileged_agent_required: {
    title: 'Only the privileged agent may do this',
    body: 'Sensitive work needs the privileged-operations agent, or a granted agent acting under a dual-control record that names the approving human (amendment 0002). This call had neither.',
    who: 'Superadmin',
    tone: 'danger',
  },
  dual_control_required: {
    title: 'This needs an approval that names a person',
    body: 'A sensitive call carries a reference to a decision somebody recorded — a draft they approved, an action they confirmed. This one carried none, or one that did not resolve.',
    who: 'The person whose decision it is',
    tone: 'danger',
  },
  rate_limited: {
    title: 'Too fast',
    body: 'Sensitive work carries its own rate limits. This will go through shortly; nothing was lost.',
    who: 'Nobody — wait',
    tone: 'neutral',
  },
  daily_ceiling_reached: {
    title: 'Enough for one day',
    body: 'The surface behind this tool allows a person so many a day, and the agent has done that many (§19). It resumes tomorrow; nothing was lost.',
    who: 'Nobody — it resumes',
    tone: 'neutral',
  },
  monthly_ceiling_reached: {
    title: 'This month’s allowance is used',
    body: 'The vendor behind this tool allows so many calls a month, and they have been made. It resumes when the month turns; nothing was lost.',
    who: 'Nobody — it resumes, or a superadmin moves to a larger plan',
    tone: 'neutral',
  },
  allowance_reached: {
    title: 'What is free here is used',
    body: 'This vendor’s free allowance, less a margin, is used for now, and nothing paid is used unless a superadmin allows it. It opens again when the period turns; nothing was lost.',
    who: 'A superadmin, in integration settings — or wait for the period to turn',
    tone: 'neutral',
  },
  plan_ended: {
    title: 'The plan with this vendor has ended',
    body: 'The plan’s last day has passed, so nothing it gave free is free, and no paid use is allowed after it.',
    who: 'A superadmin, who records the next plan in integration settings',
    tone: 'warning',
  },
  vendor_paused: {
    title: 'This vendor is paused',
    body: 'The platform paused it on a rule — for Customer.io, an email it reported sending, which policy allows none of — or a superadmin did. Nothing reaches it until a superadmin resumes it.',
    who: 'A superadmin, in integration settings',
    tone: 'warning',
  },
  project_tool_forbidden: {
    title: 'This department has forbidden that tool',
    body: 'A project lent this tool to its participants, but your own department has explicitly put it on its never: list — a project can never lend past that, whatever it names (Amendment 0008).',
    who: 'A superadmin, if this department\'s own scopes should change',
    tone: 'warning',
  },
  task_tool_forbidden: {
    title: 'A department of yours has forbidden that tool',
    body: 'A superadmin granted this tool to your twin’s task, but one of your own departments has put what it needs on its never: list — a task is never granted past that, whatever the grant says (Amendment 0011).',
    who: 'A superadmin, if that department\'s own scopes should change',
    tone: 'warning',
  },
  keeper_only: {
    title: 'Only a superadmin’s own twin reads the platform itself',
    body: 'This reads the platform’s own cloud project or repository. Only a superadmin’s own twin does that, under the standing assignment they confirmed (Amendment 0013).',
    who: 'A superadmin',
    tone: 'danger',
  },
} as const satisfies Record<string, Denial>;

export type DenyReason = keyof typeof DENIALS;

export const UNKNOWN_DENIAL: Denial = {
  title: 'Refused',
  body: 'The gateway refused this for a reason this interface does not yet have words for. The reason is shown as it arrived.',
  who: 'Platform',
  tone: 'warning',
};

export function denialFor(reason: string): Denial {
  return (DENIALS as Record<string, Denial>)[reason] ?? UNKNOWN_DENIAL;
}
